import { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-32 lg:py-40 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="rsl-container relative z-10">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Get In Touch
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight mt-2">
            Contact Us
          </h1>
          <p className="text-white/80 text-lg max-w-xl mt-6">
            Have questions about our services? Our team is ready to help you with all your shipping and logistics needs.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-navy mb-8">Contact Information</h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="bg-safety-orange/10 p-3 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-safety-orange" />
                  </div>
                  <div>
                    <h3 className="font-bold text-navy mb-1">Address</h3>
                    <p className="text-slate-600">
                      967 E 12th St<br />
                      Los Angeles, CA 90021<br />
                      United States
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-safety-orange/10 p-3 flex-shrink-0">
                    <Phone className="w-6 h-6 text-safety-orange" />
                  </div>
                  <div>
                    <h3 className="font-bold text-navy mb-1">Phone</h3>
                    <a
                      href="tel:+14153299700"
                      className="text-slate-600 hover:text-safety-orange transition-colors"
                      data-testid="contact-phone"
                    >
                      (415) 329-9700
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-safety-orange/10 p-3 flex-shrink-0">
                    <Mail className="w-6 h-6 text-safety-orange" />
                  </div>
                  <div>
                    <h3 className="font-bold text-navy mb-1">Email</h3>
                    <a
                      href="mailto:info@robertshipping.com"
                      className="text-slate-600 hover:text-safety-orange transition-colors"
                      data-testid="contact-email"
                    >
                      info@robertshipping.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-safety-orange/10 p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-safety-orange" />
                  </div>
                  <div>
                    <h3 className="font-bold text-navy mb-1">Business Hours</h3>
                    <p className="text-slate-600">
                      Monday - Friday: 8:00 AM - 6:00 PM PST<br />
                      Saturday: 9:00 AM - 2:00 PM PST<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-8 bg-slate-100 h-64 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Los Angeles, CA</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 p-8 lg:p-12">
                <h2 className="text-2xl font-bold text-navy mb-2">Send Us a Message</h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>

                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-navy mb-2">Message Sent!</h3>
                    <p className="text-slate-600 mb-6">
                      Thank you for contacting us. We'll respond to your inquiry within 24 hours.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-2 border-navy text-navy hover:bg-navy hover:text-white"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                          Full Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                          placeholder="John Doe"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                          placeholder="john@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                          placeholder="+1 (555) 000-0000"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                          Subject *
                        </label>
                        <Input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="h-12 border-2 border-slate-200 focus:border-safety-orange"
                          placeholder="How can we help?"
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="border-2 border-slate-200 focus:border-safety-orange resize-none"
                        placeholder="Tell us about your shipping needs..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
